/**
 * Created by Christiaan on 01/03/2017.
 */
import {
    AfterViewInit,
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild
} from '@angular/core';

import {GlobalAlertService} from "../global-alert.service";
import {GlobalPopup} from "../global-popup";
import {HTTPError} from "../../../shared/services/http/http-error";
import {RequestFailure} from "../../../shared/services/http/request-failure";
import {FormDataService} from "../../../shared/components/form/services/form-data.service";
import {GlobalModel} from "../../../shared/services/state/global.model";
import {TranslateService} from "../../../shared/services/translate/translate.service";
import {AbstractBasePopupComponent} from "./abstract-base-popup.component";
import {TooltipService} from "../../../shared/services/tooltip/tooltip.service";
import { FormEvent } from '../../../shared/components/form/containers/form/form.interface';
import {LoggerService} from "../../../shared/services/logger/logger.service";
import {environment} from "../../../../environments/environment";
import {DefaultPopupWrapperComponent} from "./default-popup-wrapper/default-popup-wrapper.component";

@Component ({
    selector: 'global-popup-create-public-report-component',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <default-popup-wrapper-component #wrapper [hideButtons]="config && config.hasWorkOrder" [componentRef]="this" [disableButtons]="readOnly" [disableButtonsOverride]="disableButtonsOverride" [globalPopup]="globalPopup" [showLoader]="isLoading" (onClose)="closePopup($event)">
            <div class="pr-0">
                <div *ngIf="formData && config">
                    <form-component #form
                            [config]="config" 
                            [label]="formData.schema.label"
                            [name]="getFormName()"
                            [validationConstraints]="validationConstraints"
                            [invalidControlsErrors]="invalidControlsErrors"
                            [readOnly]="readOnly"
                            [formIsSubmitted]="formIsSubmitted"
                            (onComponentEvent)="handleComponentEvent($event)">
                    </form-component>
                    <div *ngIf="!config.hasWorkOrder" class="mt-2 d-flex justify-content-end">
                        <ngx-turnstile
                            [siteKey]="turnstileSiteKey"
                            action="report_malfunction"
                            theme="light"
                            (resolved)="onTurnstileResolved($event)">
                        </ngx-turnstile>
                    </div>
                </div>
            </div>
        </default-popup-wrapper-component>
    `
})

export class GlobalPopupCreatePublicReportComponent extends AbstractBasePopupComponent implements AfterViewInit
{
    @Input() requestUrl:string = "";
    @Input() submitUrl:string = "";

    @ViewChild('form', {static: false}) form:any;
    @ViewChild('wrapper', {static: false}) wrapper!:DefaultPopupWrapperComponent;

    public turnstileSiteKey:string = environment.turnstileSiteKey;
    private captchaToken: string = '';

    // Keep default popup button behavior, but lock submit until captcha is solved.
    public disableButtonsOverride = (button: any): boolean => {
        if (this.readOnly || button?.disabled) {
            return true;
        }

        if (!this.config?.hasWorkOrder && button?.role === GlobalAlertService.BUTTON_ROLE_SUBMIT && this.captchaToken === '') {
            return true;
        }

        return false;
    };

    public constructor(protected cd:ChangeDetectorRef, protected globalAlertService:GlobalAlertService, protected formDataService:FormDataService, protected elementRef:ElementRef, private model:GlobalModel, protected ts:TranslateService, protected tooltipService:TooltipService, protected logger:LoggerService) {
        super(cd, globalAlertService, elementRef, ts, tooltipService, logger);
    }

    ngAfterViewInit(){

        //Retrieve map item id from the popup data
        this.requestUrl = this.globalPopup.data.requestUrl;
        this.submitUrl = this.globalPopup.data.submitUrl;

        let popupData:any = this.globalPopup.data;
        if (popupData && popupData.lat && popupData.lng){
            this.model.formLocationComponentPosition = {lat:popupData.lat, lng:popupData.lng};
        }else{
            this.model.formLocationComponentPosition = null;
        }

        const submitButton = this.getSubmitButton();
        if (submitButton) {
            this.submitButton = submitButton;
        }

        this.requestFormData();
    }

    handlePopupAction(event: MouseEvent | null, alert: GlobalPopup, button: any): void {
        //callback for submit button comes from this form, not from outside this component
        if (button == this.submitButton){
            this.form.handleComponentEvent({event:FormEvent.SAVE, data:{}});
            //this.form.submitForm();
        }else{
            //Hide alert and perform callback of button
            const actionEvent = event ?? new MouseEvent('click');
            this.onPopupAction.emit({event: actionEvent, alert:alert, button:button});
        }
    }
    
    public handleComponentEvent(eventData:any):void
    {
        switch (eventData.event) {
            case FormEvent.SAVE:
                this.submitFormData(eventData.data.formData);
                break;
        }
    }

    private submitFormData(form: any):void
    {
        if (!this.captchaToken) {
            this.globalAlertService.addPopup(
                this.ts.translate('Let op:'),
                this.ts.translate('turnstile.required'),
                [{label: this.ts.translate('Ok'), code: 'OK', isPrimary: true}],
                () => {}
            );
            return;
        }

        form.turnstileToken = this.captchaToken;
        this.handleSubmitForm();

        this.formDataService.setFormData(form, this.getFormName(), this.submitUrl,
            (formData:any) => {
                //this.formData = formData;
                this.handleSubmitResponse();

                //Hide alert and perform success callback via submit button
                const syntheticEvent = new MouseEvent('click');
                this.onPopupAction.emit({event: syntheticEvent, alert:this.globalPopup, button:this.submitButton, data:formData}); //, data:{lat:form.location.latitude, lng:form.location.longitude}});
            },
            (failure: RequestFailure) => {
                this.logger.log("[GlobalPopupCreateBaseObjectComponent] " + "Handle failure response: ", failure);
                this.captchaToken = '';
                this.handleSubmitResponse(failure.formErrors);
            },
            (error:HTTPError) => {
                this.captchaToken = '';
                this.handleSubmitResponse(null, true);
            },
            false
        );
    }

    public onTurnstileResolved(token:string):void
    {
        this.captchaToken = token ?? '';
        this.cd.detectChanges();
        // Wrapper is OnPush and the disableButtonsOverride ref never changes, so force it to re-evaluate the button state
        this.wrapper?.runChangeDetection();
    }

    public closePopup(event:any):void
    {
        // Only show the warning for the input form
        let showCloseWarning:boolean = !this.config.hasWorkOrder;
        this.doDefaultCloseHandling(event, showCloseWarning);
    }

    public requestFormData():void
    {
        this.formDataService.getFormData(this.requestUrl,
            (formData:any) => {

                //TODO: pas hier de latlng van de popup.data toe op de formData, als beschikbaar
                //En update dus de kaart en de locatie enzo

                //setTimeout(() => {
                    this.formData = formData;
                    this.isLoading = false;
                    this.cd.detectChanges();
                //});
            },
            () => {
                this.closePopup(null);
            },
            () => {
                this.closePopup(null);
            },
            false,
            false
        );
    }
}
